export interface SmartPricingWindow {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  value: number;
  valueType: 'percentage' | 'fixed';
}

export interface SmartPricingRule {
  appliesTo: 'all_business' | 'services' | 'providers';
  windows: SmartPricingWindow[];
}

export interface TimeRange {
  startTime: string;
  endTime: string;
}

export interface AppDiscountDisplay {
  valueType: 'percentage' | 'fixed';
  value: number;
  isMax: boolean;
  days: number[];
  timeRanges: TimeRange[];
}

const daysOf = (windows: SmartPricingWindow[]): number[] =>
  [...new Set(windows.map((w) => w.dayOfWeek))].sort((a, b) => a - b);

const timeRangesOf = (windows: SmartPricingWindow[]): TimeRange[] =>
  windows.map((w) => ({ startTime: w.startTime, endTime: w.endTime }));

// Varied % rule → the highest ("Hasta X%"); varied fixed amounts or mixed % and fixed have no honest single value → null.
export const getRuleAppDiscount = (rule: SmartPricingRule | null | undefined): AppDiscountDisplay | null => {
  if (!rule) {
    return null;
  }

  const percentageWindows = rule.windows.filter((w) => w.valueType === 'percentage');
  const fixedWindows = rule.windows.filter((w) => w.valueType === 'fixed');

  if (percentageWindows.length > 0 && fixedWindows.length > 0) {
    return null;
  }

  if (percentageWindows.length > 0) {
    const values = percentageWindows.map((w) => w.value);
    const uniform = values.every((v) => v === values[0]);

    return {
      valueType: 'percentage',
      value: uniform ? values[0] : Math.max(...values),
      isMax: !uniform,
      days: daysOf(percentageWindows),
      timeRanges: timeRangesOf(percentageWindows),
    };
  }

  if (fixedWindows.length > 0) {
    const values = fixedWindows.map((w) => w.value);

    if (!values.every((v) => v === values[0])) {
      return null;
    }
    return {
      valueType: 'fixed',
      value: values[0],
      isMax: false,
      days: daysOf(fixedWindows),
      timeRanges: timeRangesOf(fixedWindows),
    };
  }

  return null;
};

// `dayNames` goes from 0=Sunday to 6=Saturday, like smart pricing's `day_of_week`.
export const formatDiscountDays = (days: number[], dayNames: string[], connector: string): string => {
  const names = [...new Set(days)]
    .filter((d) => d >= 0 && d <= 6)
    .sort((a, b) => a - b)
    .map((d) => dayNames[d])
    .filter(Boolean);

  if (names.length === 0) {
    return '';
  }
  if (names.length === 1) {
    return names[0];
  }

  return `${names.slice(0, -1).join(', ')} ${connector} ${names[names.length - 1]}`;
};

const toMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);

  return (h || 0) * 60 + (m || 0);
};

const isFullDayRange = (range: TimeRange): boolean => {
  const start = toMinutes(range.startTime);
  let end = toMinutes(range.endTime);

  if (end <= start) {
    end += 24 * 60;
  }
  return start === 0 && end >= 23 * 60 + 59;
};

// A single time range shared by all windows; a whole-day range is not advertised.
export const uniqueTimeRange = (timeRanges: TimeRange[]): TimeRange | null => {
  if (!timeRanges.length) {
    return null;
  }
  const [first] = timeRanges;
  const allSame = timeRanges.every((r) => r.startTime === first.startTime && r.endTime === first.endTime);

  if (!allSame) {
    return null;
  }
  return isFullDayRange(first) ? null : first;
};
